<template>
  <div class="tab-content scrollbar is-scrollable">
    <section class="detail-section">
      <div class="item-info">
        <div class="item-id">
          {{ transferInfo.id }}
        </div>

        <div class="item-value">
          <div
            class="item-status"
            :title="transferInfo.status"
            :class="itemCssClasses(transferInfo.status)"
          />

          <input
            class="item-label transfer-label"
            type="text"
            id="label-transfer"
            name="label-transfer"
            :class="inputClass"
            placeholder="Choose a nice name for your transfer"
            v-model="transferFormLabel"
            :title="transferFormLabel"
            v-validate="'min:1|max:200|required'"
            @blur="handleResetFieldOnLeave('label', transferInfo)"
            @keyup.enter="editTransferInfo(null, 'label', null, $event)"
          >

          <span class="form-validate-error">
            {{ errors.first('label-transfer') }}
          </span>
        </div>
      </div>

      <div class="sub-header l-flex-row--between">
        <div class="sub-header-title">
          Details
        </div>
      </div>

      <div class="info-content">
        <!-- START DATE - END DATE -->
        <div class="info-block">
          <div class="label">
            Date
          </div>

          <div class="info-block-date l-flex-row l-align-center">
            <div class="date-start">
              {{ transferInfo.createdAt }}
            </div>

            <svg-icon
              class="date-separator"
              name="arrow-direction_right"
              size="small"
            />

            <div class="date-end" v-if="!monitoringActionAllowed">
              {{ transferInfo.endDate }}
            </div>

            <div class="item-value" v-else>
              <el-date-picker
                class="date-end"
                v-model="transferFormEndDate"
                name="end_date"
                id="end_date"
                :editable="true"
                :clearable="false"
                type="date"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                placeholder="Transfer end date"
                :picker-options="endDateOptions"
                v-validate="'date_format:yyyy-MM-dd|required'"
                @blur="handleResetFieldOnLeave('endDate', transferInfo)"
                @change="(value) => editTransferInfo(null, 'endDate', value, null)"
              />
            </div>

            <span class="form-validate-error">
              {{ errors.first('end_date') }}
            </span>
          </div>
        </div>

        <!-- EMAIL -->
        <div class="info-block">
          <div class="label">
            Email
          </div>

          <div class="item-value">
            <input
              class="email-input"
              :class="inputClass"
              id="transfer-email"
              name="transfer-email "
              type="text"
              :placeholder="transferFormEmail"
              v-model="transferFormEmail"
              :title="transferFormEmail"
              v-validate="'min:1|max:255|required|email'"
              @blur="handleResetFieldOnLeave('email', transferInfo)"
              style="text-overflow: ellipsis; width: 100%; padding-left: 0"
              @keyup.enter="editTransferInfo(null, 'email', null, $event)"
            >

            <span
              class="form-validate-error"
              v-if="errors.first('transfer-email')"
            >
              The transfer email field must be a valid email
            </span>
          </div>
        </div>

        <!-- CC MAIL -->
        <div class="info-block">
          <div class="label">
            <span>Cc</span>
            <span class="list-counter" v-if="monitoringActionAllowed">
              {{ transferCcToArray(transferFormCc).length }} / 10
            </span>
          </div>

          <div
            class="item-value cursor"
            v-if="transferCcToArray(transferFormCc).length >= 1"
            :class="{ cursor: monitoringActionAllowed }"
            @click="displayEdit('cc', transferInfo)"
          >
            <div class="value value-list cc-email">
              <span
                class="value-item"
                v-for="(cc, idx) in transferCcToArray(transferFormCc)"
                :key="idx"
                :title="cc"
                :class="{ 'no-edit': !monitoringActionAllowed }"
              >
                {{ cc }}
              </span>
            </div>

            <svg-icon
              v-if="monitoringActionAllowed"
              class="cursor edit-action"
              name="edit"
              size="small"
            />
          </div>

          <button
            class="btn--xsmall btn-add"
            v-if="
              !displayEditSingleInput.cc &&
                transferCcToArray(transferFormCc).length === 0 &&
                monitoringActionAllowed
            "
            @click="displayEdit('cc', transferInfo)"
          >
            + add
          </button>
        </div>

        <!-- MODAL SINGLE EDIT -->
        <transition name="slide-fade">
          <div
            class="edit-single-wrapper"
            v-if="displayEditSingleInput.cc"
          >
            <EmailCcField
              parent="Transfers"
              placeholder="Add, edit an email cc"
              monitoring-type="transfer"
              :default-values="defaultCcValues"
              :max-items="10"
              @formCcListUpdate="updateFormCcList"
            />

            <footer class="l-flex-row--rev">
              <button
                class="btn-cancel btn--small btn--round"
                @click="closeEmailCcWrapper"
              >
                cancel
              </button>

              <button
                class="btn btn-pale btn--small btn--round"
                @click="editTransferInfo(null, 'cc', null)"
                :class="{ 'ui-disabled': disableCcSave }"
              >
                edit
              </button>
            </footer>
          </div>
        </transition>
        <div v-if="transferType(transferInfo) === 'Partner'">
          <div class="info-block">
            <div class="label">
              Delivering type
            </div>
            <div class="value">
              {{ transferInfo.delivering_type }}
            </div>
          </div>
          <div class="info-block">
            <div class="label">
              Cookie split
            </div>
            <div class="value">
              {{ formatCookieSplit(transferInfo.cookie_split) }}
            </div>
          </div>
          <div class="info-block" v-if="transferInfo.reference_id">
            <div class="label">
              Reference ID
            </div>
            <div class="value" :title="transferInfo.reference_id">
              {{ transferInfo.reference_id }}
            </div>
          </div>

          <div class="info-block">
            <div class="label">
              CPM
            </div>
            <div class="value">
              {{ transferInfo.cpm ? transferInfo.cpm : '-' }}
            </div>
          </div>
          <div class="info-block">
            <div class="label">
              Media rate
            </div>
            <div class="value">
              {{ transferInfo.media_rate ? transferInfo.media_rate : '-' }}
            </div>
          </div>
          <div class="info-block" v-if="displayPushOfStock">
            <div class="label">
              Push of stock
            </div>

            <div
              class="value"
              v-if="(isWeboAdmin || isWeboEmployee || (monitoringActionAllowed && !audienceInfo.isThirdParty))
                && !isPushOfStockQuotaExceeded
                && transferInfo.bootstrap_status === 'not_applicable'
                && !isTransferExpired(transferInfo)"
            >
              <button
                class="btn-pushofstock btn-pale btn--small btn--round"
                @click="pushOfStock(transferInfo)"
              >
                Push
              </button>
            </div>

            <div
              class="value exceeded"
              v-else-if="isPushOfStockQuotaExceeded
                && transferInfo.bootstrap_status === 'not_applicable'
                && !isTransferExpired(transferInfo)"
            >
              <span class="form-validate-error" :title="dailyQuotaReachedMessage">
                {{ dailyQuotaReachedMessage }}
              </span>
            </div>

            <div class="value" v-else-if="isTransferExpired(transferInfo)">
              {{ transferInfo.bootstrap_status }}
            </div>

            <div class="value" v-else>
              {{ transferInfo.bootstrap_status }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECOND DETAIL SECTION -->
    <section class="detail-section">
      <div class="sub-header l-flex-row--between">
        <div class="sub-header-title">
          {{ transferType(transferInfo) }}
        </div>
      </div>

      <div class="info-content">
        <template v-if="transferType(transferInfo) === 'Partner'">
          <div class="info-block">
            <div class="label">
              Partner Name
            </div>
            <div class="value">
              {{ getTransferAccountDsps(transferInfo, 'partnerLabel') }}
            </div>
          </div>

          <div class="info-block">
            <div class="label">
              Partner account label
            </div>
            <div class="value">
              {{ getTransferAccountDsps(transferInfo, 'label', 'N/A') }}
            </div>
          </div>

          <div class="info-block">
            <div class="label">
              Partner account id
            </div>
            <div class="value">
              {{ getTransferAccountDsps(transferInfo, 'dsp_client_id') }}
            </div>
          </div>

          <div class="info-block">
            <div class="label">
              Partner segment id
            </div>
            <div class="value">
              {{ formatDSPSegmentId(transferInfo.dsp_segment_id) }}
            </div>
          </div>
        </template>

        <template v-else>
          <div class="info-content">
            <div class="info-block">
              <div class="label">
                Pixel url
              </div>
              <div
                class="value css-cut-long-text"
                :title="removeRandomParam(transferInfo.pixel_url)"
              >
                {{ removeRandomParam(transferInfo.pixel_url) }}
              </div>
            </div>

            <switchItem
              class="info-block switch-random l-flex-box l-align-center"
              :class="{ 'ui-disabled': !monitoringActionAllowed }"
              label="Random"
              input-name="pixelUrlRandom"
              :is-switch-active="setSwitchActive(transferInfo)"
              :type="transferInfo.pixel_url"
              :load-data="editPixelUrl"
              :key="transferInfo.id"
              :no-flex-between="true"
              ref="switch-transfer"
            />
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import dayjs from 'dayjs'
import { isEmpty, isEqual,  get, isNil, join, includes, toNumber } from 'lodash'
import { PIXEL_URL_RANDOM_PARAM } from '@/utils/constants'
import EmailCcField from '@/components/common/EmailCcField'
import SwitchItem from '@/components/common/SwitchItem'

export default {
    name: 'MonitoringTransfers',
    created() {
        if (this.transferInfo) this.upDateTransferForm()
    },
    components: {
        EmailCcField,
        SwitchItem
    },
    data() {
        return {
            displayEditSingleInput: {
                label: false,
                endDate: false,
                email: false,
                cc: false,
                bootstrap: false
            },
            transferFormLabel: null,
            transferFormCc: [],
            transferFormEmail: null,
            transferFormEndDate: null,
            defaultCcValues: [],
            isTransferEditing: false
        }
    },
    props: {
        transferInfo: { type: Object, required: true },
        audienceInfo: { type: Object, required: true },
    },
    watch: {
        $route: {
            handler: function(newValue) {
                if (newValue.path !== '/' + this.accountId + '/monitoring/transfers') {
                    this.handleResetFieldOnLeave('label', this.transferInfo)
                    this.handleResetFieldOnLeave('email', this.transferInfo)
                }
            },
            deep: true
        },
        selectedTransferRowId: {
            handler: function(newValue, oldValue) {
                if (!isNil(newValue) && !isEqual(newValue, oldValue)) {
                    // this.setSelectedAudienceHaveDeletedCriteria({ result: false })
                    //to check if bootstrap start and push of stock buttons must be disabled or not
                    this.retrieveAttributionCounterTransfer()
                }
            }
        },
        transferInfo: {
            handler: function(newValue) {
                if (!isNil(newValue)) {
                    this.transferFormStatus = newValue.status
                    this.transferFormLabel = newValue.label
                    this.transferFormEndDate = dayjs(newValue.endDate).format('YYYY-MM-DD')
                    this.transferFormEmail = newValue.email
                    this.transferFormCc = isEmpty(newValue.cc) ? [] : newValue.cc.split(',')
                }
            },
            deep: true
        },
        activeTab: {
            handler: function(newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (newValue === 'information') {
                        //to check if bootstrap start and push of stock buttons must be disabled or not
                        this.retrieveAttributionCounterTransfer()
                    }
                }
            }
        },
    },

    computed: {
        ...mapState('ui/transfers', {
            activeTab: state => state.activeTab,
            selectedTransferRowId: state => state.selectedTransferRowId
        }),
        ...mapState('data/accountsDsps', {
            accountsDsps: state => state.list
        }),
        ...mapState('data/transfer', {
            transferList: state => state.list
        }),
        ...mapGetters('account', ['getIsMonitoringActionAllowed']),
        ...mapState('ui/settings', {
            bootstrapPush: state => state.general.bootstrapPush
        }),
        ...mapState('account', {
            accountId: state => state.accountId,
            attributionCounterTransfer: state => state.attributionCounterTransfer,
            userRole: state => state.userRole,
            dailyPushOfStockLimit: state => state.dailyPushOfStockLimit
        }),

        inputClass() {
            return this.monitoringActionAllowed ? 'edit-single-input' : ' ui-disabled no-edit'
        },

        monitoringActionAllowed() {
            return this.getIsMonitoringActionAllowed()
        },

        isWeboAdmin() {
            return this.userRole === 'webo_admin'
        },
        isWeboEmployee() {
            return this.userRole === 'webo_employee'
        },

        displayPushOfStock() {
            const { type } = this.transferInfo
            if (this.isWeboAdmin) return true
            if  (this.isWeboEmployee && this.bootstrapPush) return true
            if (type === 'cclones' && this.bootstrapPush) return true
            if (this.bootstrapPush && this.monitoringActionAllowed) return true
            return false
        },

        // CC
        disableCcSave() {
            if (this.defaultCcValues) return isEqual(this.defaultCcValues, this.transferFormCc)
            return this.transferFormCc === 0
        },

        isPushOfStockQuotaExceeded() {
            //checking if adding this current Volume would exceed daily push of stock limit
            if (this.userRole !== 'webo_admin') return this.transferInfo.volume + this.attributionCounterTransfer >= this.dailyPushOfStockLimit
            //webo admin are not affected by quotas
            return false
        },

        endDateOptions() {
            return {
                firstDayOfWeek: 1,
                disabledDate(time) {
                    return (
                        dayjs()
                            .startOf('day')
                            .valueOf() >
                            dayjs(time)
                                .startOf('day')
                                .valueOf() ||
                        dayjs(time).valueOf() >=
                           dayjs()
                               .add(395, 'day')
                               .startOf('day')
                               .valueOf()
                    )
                }
            }
        },

        dailyQuotaReachedMessage() {
            return 'The account daily quota has been reached or would be exceeded. Bootstrap will be available when the quota is reset.'
        }
    },

    methods: {
        ...mapMutations('ui/transfers', ['setShowFormTransfer', 'setShowFormCreateTransfer', 'setDisplayModal', 'setDeletedTransfer']),
        ...mapMutations('data/transfer', ['setTransferInfos']),
        ...mapActions('account', ['retrieveAttributionCounterTransfer']),
        ...mapActions('data/transfer', ['editTransfer', 'deleteTransfer', ]),
        ...mapActions('dashboardAccount', ['retrieveExpiringAudiences', 'retrieveExpiringTransfers']),

        transferType(transfer) {
            return transfer.pixel_url !== '' ? 'Pixel' : 'Partner'
        },
        itemCssClasses(element) {
            let cssClasses = ''
            if (element === 'inactive') cssClasses += ' is-inactive'
            if (element === 'active') cssClasses += ' is-active'

            return cssClasses
        },
        editTransferInfo(transferURL, dataToEdit, newValue, event) {
            this.$validator.validateAll().then(isValid => {
                if (isValid) {
                    let _params
                    if (dataToEdit === 'endDate') {
                        if (isNil(this.transferFormEndDate)) {
                            this.isTransferEditing = false
                            return
                        }
                        _params = { params: { end_date: newValue }, notificationMessage: 'The date has been modified' }
                    } else if (dataToEdit === 'cc')
                        _params = { params: { cc: join(this.transferFormCc, ',') }, notificationMessage: 'The email_cc has been modified' }
                    else if (dataToEdit === 'label') {
                        _params = { params: { label: this.transferFormLabel }, notificationMessage: 'The label has been modified' }
                    } else if (dataToEdit === 'email')
                        _params = { params: { email: this.transferFormEmail }, notificationMessage: 'The email has been modified' }
                    else if (dataToEdit === 'pixelUrlRandom') {
                        if (!newValue) {
                            //remove random param from pixel_url
                            _params = {
                                params: { pixel_url: this.removeRandomParam(transferURL) },
                                notificationMessage: 'The random parameter has been disabled'
                            }
                        } else {
                            //add random param from pixel_url
                            _params = {
                                params: { pixel_url: this.addRandomParam(transferURL) },
                                notificationMessage: 'The random parameter has been enabled'
                            }
                        }
                    }

                    this.editTransfer({
                        ..._params,
                        audienceId: this.transferInfo.audience_id,
                        id: this.transferInfo.id
                    }).then(() => {
                        this.isTransferEditing = false
                        if (dataToEdit === 'email' || dataToEdit === 'label') {
                            event.target.blur()
                        }
                        if (dataToEdit === 'endDate') this.transferFormEndDate = newValue
                        if (dataToEdit === 'label') this.transferFormLabel = _params?.params?.label
                        if (dataToEdit === 'email') this.transferFormEmail = _params?.params?.email
                        if (dataToEdit === 'cc') this.defaultCcValues = this.transferFormCc
                        this.retrieveExpiringAudiences()
                        this.retrieveExpiringTransfers()
                    }).catch((error) => {
                        if(error.apiError.message === 'DataTransfer already exists with same criterias'){
                            // reset value switch item
                            this.$refs['switch-transfer'].isActive = !this.$refs['switch-transfer'].isActive
                        }
                    })

                    // close edit modal
                    if (dataToEdit !== 'pixelUrlRandom') this.displayEditSingleInput[dataToEdit] = false
                }
            })
        },
        editPixelUrl(id, transferURL, _type, newValue) {
            let params = {}
            if (!newValue) {
                //remove random param from pixel_url
                params = {
                    params: { pixel_url: this.removeRandomParam(transferURL) },
                    notificationMessage: 'The random parameter has been disabled'
                }
            } else {
                //add random param from pixel_url
                params = {
                    params: { pixel_url: this.addRandomParam(transferURL) },
                    notificationMessage: 'The random parameter has been enabled'
                }
            }
            this.editTransfer({
                ...params,
                audienceId: this.transferInfo.audience_id,
                id
            }).catch((error) => {
                if(error.apiError.message === 'DataTransfer already exists with same criterias'){
                    // reset value switch item
                    this.$refs['switch-transfer'].isActive = !this.$refs['switch-transfer'].isActive
                }
            })

        },
        removeRandomParam(pixelURL) {
            const _pixelUrl = pixelURL.toLowerCase()
            const url = includes(_pixelUrl, '&' + PIXEL_URL_RANDOM_PARAM.toLowerCase())
                ? _pixelUrl.split('&' + PIXEL_URL_RANDOM_PARAM.toLowerCase())
                : _pixelUrl.split('?' + PIXEL_URL_RANDOM_PARAM.toLowerCase())
            return join(url, '')
        },
        addRandomParam(pixelURL) {
            // check if URL has params
            const _params = new URL(pixelURL).searchParams
            const params = []
            for (var value of _params.values()) {
                params.push(value)
            }
            if (isEmpty(params)) {
                //no params => add random as first param
                return pixelURL + '?' + PIXEL_URL_RANDOM_PARAM
            } else {
                //params => random as last param
                return pixelURL + '&' + PIXEL_URL_RANDOM_PARAM
            }
        },
        deleteTransferHandler(transferId, audienceId) {
            this.setDeletedTransfer({ transferId, audienceId })
            this.setDisplayModal({ item: 'transfer', display: true })
        },
        isTransferExpired(transfer) {
            return dayjs().isAfter(dayjs(transfer.endDate), 'day')
        },
        // CC
        transferCcToArray(cc) {
            return isEmpty(cc) ? [] : cc
        },
        updateFormCcList(ccList) {
            this.transferFormCc = ccList
        },
        setSwitchActive(transfer) {
            return includes(transfer['pixel_url'].toLowerCase(), PIXEL_URL_RANDOM_PARAM.toLowerCase())
        },
        // Modal single edit
        displayEdit(field, transfer) {
            if (this.monitoringActionAllowed) {
                if (transfer) {
                    // edit transfer
                    if (field === 'label') this.transferFormLabel = transfer.label
                    else if (field === 'endDate') this.transferFormEndDate = transfer.end_date
                    else if (field === 'email') this.transferFormEmail = transfer.email
                    else if (field === 'cc') {
                        this.transferFormCc = isEmpty(transfer.cc) ? [] : transfer.cc.split(',')
                        this.defaultCcValues = isEmpty(transfer.cc) ? [] : transfer.cc.split(',')
                    }

                    if (!isNil(get(this.displayEditSingleInput[field], 'id', null))) {
                        this.displayEditSingleInput[field] = true
                    } else {
                        this.displayEditSingleInput[field] = true
                    }
                } else {
                    this.displayEditSingleInput[field] = true //edit audience
                }
            }
        },
        closeEmailCcWrapper() {
            this.displayEditSingleInput.cc = false
            this.transferFormCc = this.defaultCcValues
        },
        formatDSPSegmentId(dsp_segment_id) {
            return dsp_segment_id !== '' ? dsp_segment_id : '-'
        },
        formatCookieSplit(cookie_split) {
            if (cookie_split === null) return '1 to 100'
            const cookieSplitValues = cookie_split.split('..').map(x => toNumber(x) + 1)
            return cookieSplitValues.shift().toString() + ' to ' + cookieSplitValues.pop().toString()
        },
        pushOfStock(transfer) {
            this.editTransfer({
                params: { bootstrap: 'bootstrap', bootstrap_status: 'pending' },
                notificationMessage: 'Stock will be pushed to ' + transfer.partner,
                audienceId: +transfer.audience_id,
                id: transfer.id
            }).then(() => {
                this.setTransferInfos(transfer.id)
            })
        },
        upDateTransferForm() {
            this.transferFormStatus = this.transferInfo.status
            this.transferFormLabel = this.transferInfo.label
            this.transferFormEndDate = dayjs(this.transferInfo.endDate).format('YYYY-MM-DD')
            this.transferFormEmail = this.transferInfo.email
            this.transferFormCc = isEmpty(this.transferInfo.cc) ? [] : this.transferInfo.cc.split(',')
        },
        handleResetFieldOnLeave(field, transfer) {
            if (field === 'label') this.transferFormLabel = transfer.label
            if (field === 'endDate') this.transferFormEndDate = transfer.endDate
            else if (field === 'email') this.transferFormEmail = transfer.email
            this.errors.clear()
        },
        getTransferAccountDsps(transfer, key, defaultValue = '-') {
            const dspId = transfer.accounts_dsp_id
            const $dsp = this.accountsDsps.find(dsp => dsp.id === dspId)
            return $dsp?.[key] ?? defaultValue
        }
    }
}
</script>
